/**
 * One labelled control.
 *
 * Exists because the obvious markup is subtly wrong:
 *
 *   <label className="mb-2 block">Your name<input /></label>
 *
 * The margin is on the element that WRAPS the input, so it lands *below*
 * the input — the label text and its control end up flush. A standalone
 * `<span className="mb-2">` before a control does the opposite and gets the
 * gap right, which is why two fields written the two different ways sat at
 * visibly different spacings on the same panel.
 *
 * Here the text is always its own block inside the label, so the gap between
 * a label and its control is one number in one place.
 */
export const labelTextCls = "mb-2 block text-[12.5px] font-semibold text-body";

export const controlCls =
  "block w-full appearance-none rounded-[10px] border border-line-input px-3.5 py-[11px] text-sm text-ink placeholder-faint focus:border-primary-600 focus:outline-none";

export const Field = ({
  label,
  hint,
  error,
  children,
  className = "",
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`mb-5 block ${className}`}>
    <span className={labelTextCls}>{label}</span>
    {children}
    {error ? (
      <span role="alert" className="mt-1.5 block text-[12.5px] text-danger">
        {error}
      </span>
    ) : hint ? (
      <span className="mt-1.5 block text-[12.5px] leading-[1.5] text-faint">
        {hint}
      </span>
    ) : null}
  </label>
);

/** The same shape, for a group of controls that no single label owns. */
export const FieldGroup = ({
  label,
  hint,
  children,
  className = "",
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`mb-5 ${className}`}>
    <span className={labelTextCls}>{label}</span>
    {children}
    {hint ? (
      <p className="mt-1.5 text-[12.5px] leading-[1.5] text-faint">{hint}</p>
    ) : null}
  </div>
);
